import { useMemo } from 'react';
import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import SearchableSelect from '../SearchableSelect';
import TableCardWithStates from '../TableCardWithStates';
import { SearchInput } from '../SearchInput';
import { countdownPreview } from './appCardMirror';
import { TASK_TYPES, askedFor, isFigureTask, isFillIn, taskLabel, type AnnouncementBatch } from './bannerOptions';
import { tokensAsLabels } from './bannerTokens';
import type { BannerToken } from './bannerApi';
import type { SentHistory, StatusFilter } from './useSentHistory';

const STATUS_CHIPS: { value: StatusFilter; label: string; dot?: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live', dot: 'bg-green-500' },
  { value: 'ended', label: 'Ended', dot: 'bg-gray-400' },
  { value: 'removed', label: 'Removed', dot: 'bg-red-500' },
];

// Revoked is its own state — "Ended" would hide that someone pulled it, which is the
// single most important thing in this list.
const statusOf = (b: AnnouncementBatch): Exclude<StatusFilter, 'all'> =>
  b.revokedAt ? 'removed' : b.active ? 'live' : 'ended';

const STATUS_BADGE: Record<Exclude<StatusFilter, 'all'>, { label: string; className: string }> = {
  live: { label: 'Live', className: 'bg-green-50 text-green-700 border-green-200' },
  ended: { label: 'Ended', className: 'bg-gray-50 text-gray-600 border-gray-200' },
  removed: { label: 'Removed', className: 'bg-red-50 text-red-700 border-red-200' },
};

// India time, whatever the browser's zone, as the tracker says it.
const INDIA = 'Asia/Kolkata';
const dateTime = (iso: string) =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: INDIA,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} IST`;
const dateOnly = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { timeZone: INDIA, day: 'numeric', month: 'short', year: 'numeric' });
const dayMonth = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { timeZone: INDIA, day: 'numeric', month: 'short' });

// A rule above every row, as in the design. On each cell rather than between the rows, so the
// sticky Actions cell carries its own piece of it while the table scrolls under it.
const CELL = 'border-t border-gray-200 px-4 py-[11px]';
const HEAD = 'whitespace-nowrap px-4 py-3 font-medium';
// The same edge as the app's other sticky Actions columns, while there is something under it.
// From 1250px the 1200px table fits (the page's gutters and the card's border take 50px).
const ACTIONS_EDGE =
  'sticky right-0 z-10 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] min-[1250px]:shadow-none';

/** Every personalized banner and task sent on this project, newest first. */
export function SentHistoryTab({
  sent,
  onOpenTracker,
  tokens,
}: {
  sent: SentHistory;
  /** The people icon: opens this send's tracker. */
  onOpenTracker: (batchId: string) => void;
  /** So a detail in a stored title reads as its name. */
  tokens: BannerToken[];
}) {
  const {
    history,
    historyLoading,
    historyError,
    fetchHistory,
    revokingBatch,
    revokeBatch,
    query,
    setQuery,
    status,
    setStatus,
    type,
    setType,
  } = sent;

  // The app's task kinds, plus any kind an older or newer send carries.
  const typeOptions = useMemo(() => {
    const kinds = new Set<string>(TASK_TYPES.map((t) => t.value));
    for (const b of history) if (b.kind === 'task' && b.taskType) kinds.add(b.taskType);
    return [...kinds];
  }, [history]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return history.filter((b) => {
      if (status !== 'all' && statusOf(b) !== status) return false;
      if (type === 'message' && b.kind === 'task') return false;
      if (type !== 'all' && type !== 'message' && !(b.kind === 'task' && b.taskType === type)) return false;
      if (q && !b.title.toLowerCase().includes(q) && !(b.sentBy ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [history, query, status, type]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-72">
          <SearchInput value={query} onChange={setQuery} placeholder="Search title or sender" size="md" />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.value}
              type="button"
              aria-pressed={status === c.value}
              onClick={() => setStatus(c.value)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                status === c.value
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {c.dot && <span className={`h-2 w-2 rounded-full ${c.dot}`} />}
              {c.label}
            </button>
          ))}
        </div>
        <div className="w-44">
          <SearchableSelect
            value={type}
            onChange={setType}
            allowClear={false}
            options={[
              { value: 'all', label: 'All types' },
              { value: 'message', label: 'Message' },
              ...typeOptions.map((k) => ({ value: k, label: `Task · ${taskLabel(k)}` })),
            ]}
          />
        </div>
        <span className="ml-auto text-xs text-gray-500" title="The page reads the latest 50 sends.">
          Latest 50
        </span>
      </div>

      {historyError && history.length > 0 && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {historyError} This is the last list read.{' '}
          <button type="button" onClick={fetchHistory} className="font-medium underline">
            Try again
          </button>
        </p>
      )}

      {historyError && history.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
          <p>{historyError}</p>
          <button
            type="button"
            onClick={fetchHistory}
            className="mt-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      ) : (
      <TableCardWithStates
        loading={historyLoading && history.length === 0}
        isEmpty={shown.length === 0}
        emptyMessage={
          history.length === 0 ? 'Nothing sent yet. Banners you send will be listed here.' : 'No banner matches.'
        }
      >
        <div className="overflow-x-auto">
          {/* Every column but Banner has its width, so a long title gives way first and can
              never push Actions off the card. Below the minimum the table scrolls sideways,
              with Actions held in view. */}
          <table className="w-full min-w-[1200px] table-fixed border-separate border-spacing-0 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className={`${HEAD} text-left`}>Banner</th>
                <th className={`${HEAD} w-[184px] text-left`}>Type</th>
                <th className={`${HEAD} w-[108px] text-left`}>Status</th>
                <th className={`${HEAD} w-[208px] text-left`}>Sent</th>
                <th className={`${HEAD} w-[200px] text-left`}>People</th>
                <th className={`${HEAD} w-[152px] text-left`}>Until</th>
                <th className={`${HEAD} ${ACTIONS_EDGE} w-[108px] bg-gray-50`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((b) => {
                const state = statusOf(b);
                const isTaskBatch = b.kind === 'task';
                const hasFigure = isTaskBatch && isFigureTask(b.taskType);
                // Nothing marks a Read this with no button done, so it counts people, like a message.
                const tracked = b.tracksDone;
                const doneWords = isFillIn(b.reachLevel) ? 'filled in' : hasFigure ? 'reached target' : b.answers ? 'agreed' : 'done';
                const until = b.active ? 'Shows until' : 'Showed until';
                // The second line under the type, naming what was asked for.
                const typeLine = askedFor(b);
                // Taken off at its date: that is its end. Otherwise it shows on past the date.
                const endsAtDue = b.completeBy && b.afterDue === 'take_off';
                return (
                    <tr key={b.batchId} className="group hover:bg-gray-50">
                      <td className={CELL}>
                        <div className="flex min-w-0 items-start gap-2.5">
                          <span
                            className="mt-[5px] h-2.5 w-2.5 flex-none rounded-full"
                            style={{ backgroundColor: b.accentColor || '#2563EB' }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-gray-900" title={tokensAsLabels(b.title, tokens)}>
                              {tokensAsLabels(b.title, tokens)}
                            </p>
                            <p className="truncate text-xs text-gray-500" title={tokensAsLabels(b.body, tokens)}>
                              {tokensAsLabels(b.body, tokens)}
                            </p>
                          </div>
                          {b.imageBlobPath && (
                            <img
                              src={`/api/images/view/${b.imageBlobPath}`}
                              alt=""
                              className="h-9 w-9 flex-none rounded object-cover"
                            />
                          )}
                        </div>
                      </td>
                      <td className={CELL}>
                        {/* Tasks and messages share this list, so say which is which
                            before anyone reads the copy. */}
                        {isTaskBatch ? (
                          <span className="whitespace-nowrap rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700">
                            Task · {taskLabel(b.taskType)}
                          </span>
                        ) : (
                          <span className="whitespace-nowrap rounded border border-gray-200 bg-gray-50 px-1.5 py-px text-xs font-medium text-gray-700">
                            Message
                          </span>
                        )}
                        {typeLine && (
                          <p className="mt-1 truncate text-xs text-gray-500" title={typeLine}>
                            {typeLine}
                          </p>
                        )}
                      </td>
                      <td className={CELL}>
                        <span
                          className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[state].className}`}
                        >
                          {STATUS_BADGE[state].label}
                        </span>
                      </td>
                      <td className={`${CELL} whitespace-nowrap text-gray-700`}>
                        {dateTime(b.createdAt)}
                        {b.sentBy && (
                          <span className="block truncate text-xs text-gray-500" title={b.sentBy}>
                            {b.sentBy}
                          </span>
                        )}
                      </td>
                      <td className={`${CELL} text-gray-500`}>
                        {/* The number a task send exists for. Counts people who did it
                            however they did it — the ChannelPlex app or the 1Office app. */}
                        {tracked ? (
                          <span title="As of the last check. Each person's app, this send's tracker and a nightly check bring it up to date.">
                            <span className="font-medium text-gray-900">{(b.completedCount ?? 0).toLocaleString()}</span>{' '}
                            of {b.recipients.toLocaleString()} {doneWords}
                          </span>
                        ) : (
                          <span>
                            <span className="font-medium text-gray-900">{b.recipients.toLocaleString()}</span>{' '}
                            {b.recipients === 1 ? 'person' : 'people'}
                          </span>
                        )}
                        {(b.answers?.declined ?? 0) > 0 && (
                          <span className="block text-xs">{b.answers?.declined.toLocaleString()} declined</span>
                        )}
                        {b.dismissed > 0 && <span className="block text-xs">{b.dismissed} dismissed</span>}
                        {(b.overdue ?? 0) > 0 && (
                          <span className="block text-xs text-red-700">{b.overdue?.toLocaleString()} overdue</span>
                        )}
                      </td>
                      <td className={`${CELL} whitespace-nowrap text-gray-700`}>
                        {b.revokedAt ? (
                          <span title={`Removed ${dateTime(b.revokedAt)}`}>Removed {dayMonth(b.revokedAt)}</span>
                        ) : endsAtDue ? (
                          <span title={`${until} ${dateTime(b.completeBy as string)}`}>{dateOnly(b.completeBy as string)}</span>
                        ) : (
                          <span title={`${until} ${dateTime(b.expiresAt)}`}>{dateOnly(b.expiresAt)}</span>
                        )}
                        {b.completeBy && b.afterDue === 'keep_showing' && (
                          <span className="block text-xs text-gray-500" title={`Complete by ${dateTime(b.completeBy)}`}>
                            Due {dayMonth(b.completeBy)}
                          </span>
                        )}
                        {/* The countdown if one was set: a banner can be Live for a day past it.
                            The time left is behind the hover: the list does not tick, and the
                            line would widen the column. */}
                        {b.countdownTo && (
                          <span
                            className="block text-xs text-blue-700"
                            title={`Counting down to ${dateTime(b.countdownTo)}${
                              countdownPreview(b.countdownTo) ? ` · ${countdownPreview(b.countdownTo)}` : ''
                            }`}
                          >
                            ⏱ {dayMonth(b.countdownTo)}
                          </span>
                        )}
                      </td>
                      <td className={`${CELL} ${ACTIONS_EDGE} bg-white group-hover:bg-gray-50`}>
                        <TableActionGroup>
                          <TableActionButton
                            action="candidates"
                            label={tracked ? 'See who has done it' : 'See who got it'}
                            title={tracked ? 'See who has done it' : 'See who got it'}
                            onClick={() => onOpenTracker(b.batchId)}
                          />
                          {/* Only live banners can be pulled — offering it on one that has
                              already ended would imply it's still out there. */}
                          {b.active && !b.revokedAt && (
                            <TableActionButton
                              action="remove"
                              label="Remove from home screens"
                              title="Remove from home screens"
                              loading={revokingBatch === b.batchId}
                              onClick={() => revokeBatch({ ...b, title: tokensAsLabels(b.title, tokens) })}
                            />
                          )}
                        </TableActionGroup>
                      </td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </TableCardWithStates>
      )}
    </div>
  );
}
